import { Content, Distributor, Pagination } from "@/@types/general.ts";
import {
  Banner,
  CatalogFilters,
  Category,
  Collection,
  CollectionDetail,
  CollectionList,
  CollectionNoAuth,
  ContentDetail,
  ContentEpisodeInfo,
  DetailContent,
  DetailContentEpisodes,
  DetailContentRecommendation,
  DetailPageContent,
  MainGenre,
  MainList,
  Person,
  Persons,
} from "@/api/content/content.types.ts";
import { getLimitOffset } from "@/utils/funcs.ts";

import { authAxios, baseAxios, statisticAxios } from "../axios";

interface ContentRequestsParams {
  limit: number;
  offset: number;
  category?: number;
  genres?: string | null;
  search?: string | null;
  language?: string | null;
  country?: string | null;
  year?: string | null;
  ordering?: string | null;
  distributor?: string | null;
}

class ContentRequests {
  static isLoggedIn() {
    return Boolean(localStorage.getItem("splay:token"));
  }

  static async getProfileDetail(profileId: number) {
    const token = localStorage.getItem("splay:token") as string;
    return baseAxios<Content[]>(`/content/mobile/profile-selected-contents`, {
      headers: {
        Authorization: `Token ${token}`,
      },
      params: {
        profile_id: profileId,
      },
    }).then((res) => res.data);
  }

  static async getCatalogFilter(category: number) {
    const searchParams = new URLSearchParams({ id: String(category) });
    const access = localStorage.getItem("splay:access");
    return authAxios
      .get<CatalogFilters>(
        access
          ? "/content/filter-country-genre-sponsor-year-list"
          : "/content/no-auth/filter-country-genre-sponsor-year-list/",
        { params: searchParams },
      )
      .then((res) => res.data);
  }

  static async getCatalogFilterNoAuth(category?: number) {
    const searchParams = new URLSearchParams(category ? { category: String(category) } : {});
    return authAxios
      .get<CatalogFilters>("/content/no-auth/filter-country-genre-sponsor-year-list/", { params: searchParams })
      .then((res) => res.data);
  }

  static async getContent(params: ContentRequestsParams) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const searchParams = new URLSearchParams(Object.entries(params).filter(([_, value]) => value));

    return authAxios
      .get<
        Pagination<Content>
      >(this.isLoggedIn() ? "/content/web/content" : "/content/no-auth/contents/", { params: searchParams })
      .then((res) => res.data);
  }

  static async getSearchContent(params: ContentRequestsParams) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const searchParams = new URLSearchParams(Object.entries(params).filter(([_, value]) => value));

    return authAxios
      .get<
        Pagination<Content>
      >(this.isLoggedIn() ? "/content/web/content-search" : "/content/no-auth/contents/", { params: searchParams })
      .then((res) => res.data);
  }

  static async getContentDetail(id: number) {
    return authAxios
      .get<ContentDetail>(this.isLoggedIn() ? `/content/web/detail/${id}` : `/content/no-auth/contents/${id}`)
      .then((res) => res.data);
  }

  static async getBanners() {
    return authAxios
      .get<Banner[]>(this.isLoggedIn() ? "/content/web/banner" : "/content/no-auth/banner/")
      .then((res) => res.data);
  }

  static async getCategories() {
    return authAxios
      .get<Category[]>(this.isLoggedIn() ? "/content/categories" : "/content/no-auth/categories/")
      .then((res) => res.data);
  }

  static async getMainList() {
    return authAxios.get<MainList[]>("/content/web/main").then((res) => res.data);
  }
  static async getCollectionList() {
    const searchParams = new URLSearchParams({ show_contents: "true" });

    return authAxios
      .get<MainList[]>("/content/web/collections", {
        params: searchParams,
      })
      .then((res) => res.data);
  }
  static async getCollectionListNoAuth() {
    const searchParams = new URLSearchParams({ show_contents: "true" });

    return authAxios
      .get<CollectionList>("/content/no-auth/collections/", {
        params: searchParams,
      })
      .then((res) => res.data.results);
  }

  static async getPremiers(limit: number, offset: number) {
    const searchParams = getLimitOffset(limit, offset);
    return authAxios
      .get<Pagination<Content>>("/content/web/premieres", { params: searchParams })
      .then((res) => res.data);
  }

  static async getRecommends() {
    return authAxios
      .get<Content[]>("/content/web/profile-recommendations")
      .then((res) => res.data)
      .catch(() => []);
  }

  static async getPremiersNoAuth(limit: number, offset: number) {
    const searchParams = getLimitOffset(limit, offset);
    return authAxios
      .get<Pagination<Content>>("/content/no-auth/contents/?is_new=True", { params: searchParams })
      .then((res) => res.data);
  }

  static async getDistributors() {
    return authAxios
      .get<Distributor[]>(this.isLoggedIn() ? "/content/distributors" : "/content/no-auth/distributors/")
      .then((res) => res.data);
  }

  static async getGenres(limit: number, offset: number) {
    const searchParams = getLimitOffset(limit, offset);
    return authAxios
      .get<MainGenre[]>(this.isLoggedIn() ? "/content/genres" : "/content/no-auth/genres", { params: searchParams })
      .then((res) => res.data);
  }

  static async getPersons(limit: number, offset: number, search?: string) {
    const searchParams = getLimitOffset(limit, offset);
    if (search) {
      searchParams.set("search", search);
    }
    return authAxios
      .get<Persons>(this.isLoggedIn() ? "/content/persons" : "/content/no-auth/persons", { params: searchParams })
      .then((res) => res.data);
  }

  static async getPersonDetail(id: number) {
    return authAxios
      .get<Person>(this.isLoggedIn() ? `/content/web/persons/${id}` : `/content/no-auth/persons/${id}/`)
      .then((res) => res.data)
      .catch(() => null);
  }
  static async getContentDetailPage(id: number) {
    const searchParams = new URLSearchParams({ hide_prs: "True" });
    return authAxios
      .get<DetailContent>(`/content/web/full-detail/${id}`, {
        params: searchParams,
      })
      .then((res) => res.data);
  }
  static async getContentDetailPageNoAuth(id: number) {
    return authAxios.get<DetailPageContent>(`/content/no-auth/contents/${id}`).then((res) => {
      return { content: res.data };
    });
  }

  static async getContentDetailPageRecommendation(id: number) {
    return authAxios
      .get<DetailContentRecommendation>(
        this.isLoggedIn()
          ? `/content/web/full-detail/recommendations/${id}`
          : `/content/no-auth/contents/recommendations/${id}`,
      )
      .then((res) => res.data)
      .catch(() => null);
  }

  static async getContentEpisodes(id: number) {
    return authAxios
      .get<DetailContentEpisodes>(this.isLoggedIn() ? `/content/season/${id}` : `/content/no-auth/seasons/${id}`)
      .then((res) => res.data)
      .catch(() => null);
  }
  static async getContentEpisodeInfo(id: number) {
    return authAxios
      .get<ContentEpisodeInfo>(`content/episode/${id}`)
      .then((res) => res.data)
      .catch(() => null);
  }

  static async getProfileRecommendations() {
    return baseAxios<Banner[]>({
      url: "/content/web/profile-recommended-contents",
      method: "GET",
      headers: {
        Authorization: `Token ${localStorage.getItem("splay:token")}`,
      },
    }).then((res) => res.data);
  }
  static async getProfileSelectedRecommendations(id: number) {
    return baseAxios<Content[]>({
      url: `content/mobile/profile-selected-contents?profile_id=${id}`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    }).then((res) => res.data);
  }

  static async getCollections(isKids: boolean, search?: string) {
    const searchParams = new URLSearchParams({ is_kids: isKids ? "1" : "0" });

    if (search) searchParams.set("search", search);

    return authAxios.get<Collection[]>("/content/web/collections", { params: searchParams }).then((res) => res.data);
  }

  static async getCollectionsNoAuth(search?: string) {
    const searchParams = new URLSearchParams({});

    if (search) searchParams.set("search", search);

    return authAxios
      .get<CollectionNoAuth>("/content/no-auth/collections/", { params: searchParams })
      .then((res) => res.data.results);
  }

  static async getCollectionDetail({
    id,
    search,
    limit,
    offset,
  }: {
    id: number;
    search?: string;
    limit: number;
    offset: number;
  }) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let searchParams: any;

    if (search) searchParams.append("search", search);

    const token = localStorage.getItem("splay:token");

    if (!token) {
      searchParams = new URLSearchParams();

      if (limit !== undefined) searchParams.append("contents_limit", limit.toString());
      if (offset !== undefined) searchParams.append("contents_offset", offset.toString());
      searchParams.append("show_contents", "True");
    } else {
      searchParams = getLimitOffset(limit, offset);
    }

    return authAxios
      .get<CollectionDetail>(token ? `/content/web/collections/${id}` : `/content/no-auth/collections/${id}/`, {
        params: searchParams,
      })
      .then((res) => res.data);
  }

  static async getCategory(id: number) {
    return baseAxios<Category>({
      url: `content/categories/${id}`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    }).then((res) => res.data);
  }

  static async getNextContent(id: number) {
    return authAxios.get<{ content_id: number }[]>(`/content/next-content/${id}`).then((res) => res.data);
  }
  static async postContentStatistic(formData: FormData) {
    return statisticAxios
      .post(`create-history`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      })
      .then((res) => res.data);
  }
}

export default ContentRequests;
